import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Button, Paper,
    CircularProgress, Alert, Dialog, DialogTitle,
    DialogContent, DialogActions,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    IosShare as ShareIcon,
    Delete as DeleteIcon,
    CheckCircle as ConfirmedIcon,
    AccessTime as TimeIcon,
    CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import { shareOrCopy } from '../utils/share';
import { format } from 'date-fns';
import api from '../services/api';

const useScrollY = () => {
    const [scrollY, setScrollY] = useState(0);
    useEffect(() => {
        const fn = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', fn, { passive: true });
        return () => window.removeEventListener('scroll', fn);
    }, []);
    return scrollY;
};

const QuickMeetDetail = () => {
    const { id }     = useParams();
    const navigate   = useNavigate();
    const scrollY    = useScrollY();

    const [qm,           setQm]           = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [error,        setError]        = useState('');
    const [copied,       setCopied]       = useState(false);
    const [deleteOpen,   setDeleteOpen]   = useState(false);
    const [deleting,     setDeleting]     = useState(false);

    const shareUrl = qm ? `${window.location.origin}/meet/${qm.shareToken}` : '';

    useEffect(() => {
        api.get('/quick-meets')
            .then(res => {
                const found = res.data.find(q => String(q.id) === String(id));
                if (found) setQm(found);
                else setError('Quick meet not found.');
            })
            .catch(() => setError('Failed to load.'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleShare = async () => {
        const result = await shareOrCopy({ title: qm?.title, text: `Find a time to meet: ${qm?.title}`, url: shareUrl });
        if (result === 'copied') {
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await api.delete(`/quick-meets/${id}`);
            navigate('/dashboard');
        } catch {
            setError('Failed to delete.');
            setDeleteOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    if (loading) return (
        <Box sx={{ display:'flex', justifyContent:'center', alignItems:'center', height:'calc(100vh - 64px)' }}>
            <CircularProgress sx={{ color:'#7C3AED' }} />
        </Box>
    );

    if (error && !qm) return (
        <Container maxWidth="sm" sx={{ pt:8, textAlign:'center' }}>
            <Alert severity="error">{error}</Alert>
            <Button sx={{ mt:2 }} onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </Container>
    );

    const isConfirmed  = qm?.status === 'CONFIRMED';
    const confirmedSlot = qm?.confirmedSlot;

    return (
        <Box sx={{ minHeight:'100vh', bgcolor:'#F8FAFC' }}>

            {/* Header */}
            <Box sx={{ background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)', py:6, position:'relative', overflow:'clip' }}>
                <Box sx={{ position:'absolute', top:-60, left:'-4%', width:320, height:320, borderRadius:'50%', background:'rgba(255,255,255,0.07)', transform:`translateY(${scrollY*0.28}px)`, pointerEvents:'none' }} />
                <Box sx={{ position:'absolute', top:-20, right:'2%', width:240, height:240, borderRadius:'50%', background:'rgba(255,255,255,0.05)', transform:`translateY(${scrollY*0.45}px)`, pointerEvents:'none' }} />

                <Container maxWidth="lg" sx={{ position:'relative', zIndex:1, transform:`translateY(${scrollY*0.18}px)` }}>
                    <Button onClick={() => navigate('/dashboard')} startIcon={<BackIcon />}
                        sx={{ color:'rgba(255,255,255,0.7)', fontWeight:600, mb:2, px:0, '&:hover':{ color:'white', bgcolor:'transparent' } }}>
                        Dashboard
                    </Button>
                    <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:2, flexWrap:'wrap' }}>
                        <Box>
                            <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:0.5, flexWrap:'wrap' }}>
                                <Typography variant="h4" fontWeight={800} sx={{ color:'white' }}>{qm?.title}</Typography>
                                <Box sx={{ px:1.5, py:0.3, borderRadius:99, bgcolor: isConfirmed ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.15)', border: `1px solid ${isConfirmed ? 'rgba(34,197,94,0.5)' : 'rgba(255,255,255,0.3)'}` }}>
                                    <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color: isConfirmed ? '#86EFAC' : 'rgba(255,255,255,0.9)', textTransform:'uppercase', letterSpacing:0.8 }}>
                                        {isConfirmed ? 'Confirmed' : 'Pending'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography sx={{ color:'rgba(255,255,255,0.7)', fontSize:'0.95rem' }}>
                                {qm?.durationMinutes} min · {qm?.slots?.length} time option{qm?.slots?.length !== 1 ? 's' : ''}
                            </Typography>
                        </Box>
                        <Box sx={{ display:'flex', gap:1.5 }}>
                            {!isConfirmed && (
                                <Button onClick={handleShare} startIcon={<ShareIcon />} variant="contained"
                                    sx={{ fontWeight:700, borderRadius:2.5, bgcolor: copied ? 'rgba(34,197,94,0.3)' : 'rgba(255,255,255,0.18)', backdropFilter:'blur(6px)', border:`1px solid ${copied ? 'rgba(34,197,94,0.5)' : 'rgba(255,255,255,0.25)'}`, color:'white', '&:hover':{ bgcolor:'rgba(255,255,255,0.28)' }, boxShadow:'none' }}>
                                    {copied ? 'Copied!' : 'Share'}
                                </Button>
                            )}
                            <Button onClick={() => setDeleteOpen(true)} startIcon={<DeleteIcon />} variant="outlined"
                                sx={{ fontWeight:700, borderRadius:2.5, borderColor:'rgba(255,100,100,0.5)', color:'rgba(255,180,180,1)', '&:hover':{ borderColor:'rgba(255,100,100,0.9)', bgcolor:'rgba(255,60,60,0.15)' } }}>
                                Delete
                            </Button>
                        </Box>
                    </Box>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py:5 }}>
                {error && <Alert severity="error" sx={{ mb:3, borderRadius:3 }}>{error}</Alert>}

                <Box sx={{ display:'flex', gap:3, flexDirection:{ xs:'column', md:'row' }, alignItems:'flex-start' }}>

                    {/* Left: slots */}
                    <Box sx={{ flex:'1 1 0', minWidth:0 }}>
                        <Typography variant="h6" fontWeight={800} sx={{ mb:2, color:'#0F172A', letterSpacing:-0.3 }}>
                            {isConfirmed ? 'Confirmed Time' : 'Proposed Times'}
                        </Typography>

                        <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                            {qm?.slots?.map(slot => {
                                const isConfirmedSlot = confirmedSlot?.id === slot.id;
                                return (
                                    <Paper key={slot.id} elevation={0} variant="outlined" sx={{
                                        borderRadius:4, overflow:'hidden',
                                        borderColor: isConfirmedSlot ? '#22C55E' : 'rgba(0,0,0,0.1)',
                                        borderWidth: isConfirmedSlot ? 2 : 1,
                                    }}>
                                        <Box sx={{
                                            px:3, py:2.5,
                                            background: isConfirmedSlot
                                                ? 'linear-gradient(135deg,#16A34A,#15803D)'
                                                : 'linear-gradient(135deg,#7C3AED,#4F46E5)',
                                            display:'flex', alignItems:'center', justifyContent:'space-between',
                                        }}>
                                            <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                                                <Box sx={{ width:38, height:38, borderRadius:2, bgcolor:'rgba(255,255,255,0.18)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                    {isConfirmedSlot
                                                        ? <ConfirmedIcon sx={{ fontSize:20, color:'white' }} />
                                                        : <CalendarIcon sx={{ fontSize:20, color:'white' }} />}
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color:'white', lineHeight:1.2 }}>
                                                        {format(new Date(slot.proposedStartTime), 'EEEE, MMMM d, yyyy')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color:'rgba(255,255,255,0.8)' }}>
                                                        {format(new Date(slot.proposedStartTime), 'h:mm a')} – {format(new Date(slot.proposedEndTime), 'h:mm a')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {isConfirmedSlot && (
                                                <Box sx={{ px:1.5, py:0.3, borderRadius:99, bgcolor:'rgba(255,255,255,0.25)', border:'1px solid rgba(255,255,255,0.4)' }}>
                                                    <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:'white', textTransform:'uppercase', letterSpacing:0.8 }}>Confirmed</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* Right: share panel */}
                    <Box sx={{ width:{ xs:'100%', md:320 }, flexShrink:0 }}>
                        <Box sx={{ position:{ md:'sticky' }, top:24, display:'flex', flexDirection:'column', gap:2.5 }}>

                            {!isConfirmed ? (
                                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, overflow:'hidden', borderColor:'rgba(0,0,0,0.1)' }}>
                                    <Box sx={{ px:3, py:2.5, background:'linear-gradient(135deg,#7C3AED 0%,#4F46E5 100%)' }}>
                                        <Typography variant="caption" sx={{ color:'rgba(255,255,255,0.65)', textTransform:'uppercase', letterSpacing:0.8, fontWeight:700 }}>Share this link</Typography>
                                    </Box>
                                    <Box sx={{ px:3, py:3, bgcolor:'white' }}>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb:2, lineHeight:1.6 }}>
                                            Send this link to the other person. They'll pick a time and it's instantly confirmed — no back-and-forth.
                                        </Typography>
                                        <Box sx={{ p:1.5, borderRadius:2, bgcolor:'#F8FAFC', border:'1px solid', borderColor:'divider', mb:2, wordBreak:'break-all' }}>
                                            <Typography variant="caption" sx={{ fontFamily:'monospace', color:'#374151', fontSize:'0.72rem' }}>
                                                {shareUrl}
                                            </Typography>
                                        </Box>
                                        <Button fullWidth variant="contained" startIcon={<ShareIcon />} onClick={handleShare}
                                            sx={{ fontWeight:700, borderRadius:2.5, background: copied ? 'linear-gradient(135deg,#16A34A,#15803D)' : 'linear-gradient(135deg,#7C3AED,#4F46E5)', boxShadow:'none' }}>
                                            {copied ? 'Copied!' : 'Share'}
                                        </Button>
                                    </Box>
                                </Paper>
                            ) : (
                                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, p:3, borderColor:'#86EFAC', bgcolor:'#F0FDF4' }}>
                                    <Box sx={{ display:'flex', alignItems:'center', gap:1.5, mb:1.5 }}>
                                        <ConfirmedIcon sx={{ color:'#16A34A', fontSize:24 }} />
                                        <Typography variant="subtitle1" fontWeight={800} sx={{ color:'#15803D' }}>All set!</Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ color:'#166534', lineHeight:1.6 }}>
                                        This meeting has been confirmed. Both you and your guest received a confirmation email.
                                    </Typography>
                                    {confirmedSlot && (
                                        <Box sx={{ mt:2, pt:2, borderTop:'1px solid #BBF7D0' }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:0.5 }}>Confirmed for</Typography>
                                            <Typography variant="body2" fontWeight={700} sx={{ color:'#15803D' }}>
                                                {format(new Date(confirmedSlot.proposedStartTime), 'EEEE, MMM d')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color:'#166534' }}>
                                                {format(new Date(confirmedSlot.proposedStartTime), 'h:mm a')} – {format(new Date(confirmedSlot.proposedEndTime), 'h:mm a')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            )}

                            {qm?.note && (
                                <Paper elevation={0} variant="outlined" sx={{ borderRadius:4, p:3, borderColor:'rgba(0,0,0,0.1)', bgcolor:'white' }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform:'uppercase', letterSpacing:0.6, display:'block', mb:1 }}>Note</Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight:1.6 }}>{qm.note}</Typography>
                                </Paper>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Container>

            {/* Delete dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} PaperProps={{ sx:{ borderRadius:4, p:1 } }}>
                <DialogTitle sx={{ fontWeight:800, pb:1 }}>Delete Quick Meet?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        This will permanently delete <strong>{qm?.title}</strong>. If a link has already been shared, it will stop working.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px:3, pb:2.5, gap:1 }}>
                    <Button onClick={() => setDeleteOpen(false)} sx={{ borderRadius:2 }}>Cancel</Button>
                    <Button onClick={handleDelete} variant="contained" disabled={deleting}
                        sx={{ fontWeight:700, borderRadius:2.5, background:'linear-gradient(135deg,#EF4444,#DC2626)', boxShadow:'none' }}>
                        {deleting ? 'Deleting…' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default QuickMeetDetail;
